import { useCallback, useEffect, useRef, useState } from 'react'
import type { SaveStatus } from '@/hooks/saveStatus'
import { platformApi } from '../../api'
import type { Form, FormDefinition, FormVersion } from '../../types'
import { normalizeDefinition } from './model'

const DEBOUNCE_MS = 1500

export interface BuilderDraft {
    loading: boolean
    loadError: string | null
    form: Form | null
    setForm: (f: Form) => void
    /** Currently published version (for the "renaming detaches answers" warning). */
    published: FormVersion | null
    def: FormDefinition
    base: string
    hasDraft: boolean
    saveStatus: SaveStatus
    serverProblems: string[]
    dirty: boolean
    edit: (fn: (d: FormDefinition) => FormDefinition) => void
    flush: () => Promise<boolean>
    /** Drop pending saves and reload everything from the server. */
    reload: () => Promise<void>
    /** Cancel pending autosave (used before discarding the draft). */
    abandon: () => Promise<void>
}

const EMPTY: FormDefinition = { pages: [], rules: [] }

/**
 * Loads the draft and autosaves edits (debounced). Saves never overlap: edits made
 * while a PUT is in flight are saved right after it returns. Pending edits are
 * flushed on unmount.
 */
export function useBuilderDraft(formId: string): BuilderDraft {
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState<string | null>(null)
    const [form, setForm] = useState<Form | null>(null)
    const [published, setPublished] = useState<FormVersion | null>(null)
    const [def, setDef] = useState<FormDefinition>(EMPTY)
    const [base, setBase] = useState('empty')
    const [hasDraft, setHasDraft] = useState(false)
    const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
    const [serverProblems, setServerProblems] = useState<string[]>([])
    const [dirty, setDirty] = useState(false)

    const defRef = useRef(def)
    const dirtyRef = useRef(false)
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const inflight = useRef<Promise<void> | null>(null)
    const alive = useRef(true)
    const epoch = useRef(0)

    const clearTimer = () => {
        if (timer.current) clearTimeout(timer.current)
        timer.current = null
    }

    const save = useCallback(async (): Promise<boolean> => {
        clearTimer()
        // Serialize: wait for any running save, then save the latest state if still dirty.
        while (inflight.current) await inflight.current
        if (!dirtyRef.current) return true
        const myEpoch = epoch.current
        const snapshot = defRef.current
        dirtyRef.current = false
        if (alive.current) { setSaveStatus('saving'); setDirty(false) }
        let ok = true
        const p = platformApi.saveDraft(formId, snapshot).then(
            (r) => {
                if (!alive.current || myEpoch !== epoch.current) return
                setServerProblems(r.problems ?? [])
                setHasDraft(true)
                setForm((f) => (f ? { ...f, draft_version: r.version } : f))
                setSaveStatus(dirtyRef.current ? 'saving' : 'saved')
            },
            () => {
                ok = false
                dirtyRef.current = true
                if (alive.current && myEpoch === epoch.current) { setSaveStatus('error'); setDirty(true) }
            },
        ).finally(() => { inflight.current = null })
        inflight.current = p
        await p
        // Edits that arrived mid-flight.
        if (ok && dirtyRef.current && alive.current && myEpoch === epoch.current) return save()
        return ok
    }, [formId])

    const load = useCallback(async () => {
        const myEpoch = ++epoch.current
        clearTimer()
        dirtyRef.current = false
        setDirty(false)
        setLoading(true)
        setLoadError(null)
        try {
            const [d, g] = await Promise.all([platformApi.getDraft(formId), platformApi.getForm(formId)])
            if (!alive.current || myEpoch !== epoch.current) return
            const n = normalizeDefinition(d.definition)
            defRef.current = n
            setDef(n)
            setForm(d.form)
            setBase(d.base)
            setHasDraft(d.base === 'draft')
            setPublished(g.version && g.version.status !== 'draft' ? g.version : null)
            setServerProblems([])
            setSaveStatus('idle')
        } catch (e) {
            if (alive.current && myEpoch === epoch.current) setLoadError(e instanceof Error ? e.message : 'Failed to load form')
        } finally {
            if (alive.current && myEpoch === epoch.current) setLoading(false)
        }
    }, [formId])

    useEffect(() => {
        alive.current = true
        void load()
        return () => {
            alive.current = false
            clearTimer()
            // Flush pending edits (fire and forget; the component is gone).
            if (dirtyRef.current) void platformApi.saveDraft(formId, defRef.current).catch(() => undefined)
        }
    }, [formId, load])

    const edit = useCallback((fn: (d: FormDefinition) => FormDefinition) => {
        const next = fn(defRef.current)
        if (next === defRef.current) return
        defRef.current = next
        dirtyRef.current = true
        setDef(next)
        setDirty(true)
        clearTimer()
        timer.current = setTimeout(() => { void save() }, DEBOUNCE_MS)
    }, [save])

    const reload = useCallback(async () => {
        while (inflight.current) await inflight.current
        await load()
    }, [load])

    const abandon = useCallback(async () => {
        clearTimer()
        dirtyRef.current = false
        setDirty(false)
        while (inflight.current) await inflight.current
    }, [])

    return { abandon, loading, loadError, form, setForm, published, def, base, hasDraft, saveStatus, serverProblems, dirty, edit, flush: save, reload }
}
